/** Small formatting helpers shared by the list views. */

export const CONTEXTS = [
  "@komputer",
  "@dom",
  "@miasto",
  "@telefon",
  "@sklep",
  "@praca-biuro",
  "@anywhere",
];

export const NO_CONTEXT = "bez kontekstu";

/** "3 h", "2 dni", "5 tyg." - compact enough for a phone row. */
export function formatAge(hours: number): string {
  if (hours < 1) return "przed chwilą";
  if (hours < 24) return `${Math.floor(hours)} h`;
  const days = Math.floor(hours / 24);
  if (days < 14) return `${days} ${days === 1 ? "dzień" : "dni"}`;
  return `${Math.floor(days / 7)} tyg.`;
}

export function hoursSince(iso: string | null): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max((Date.now() - then) / 3_600_000, 0);
}

export function ageLabel(iso: string | null): string | null {
  const hours = hoursSince(iso);
  return hours === null ? null : formatAge(hours);
}

/** Dzisiejsza data na urządzeniu, RRRR-MM-DD (`toISOString` dałby datę w UTC). */
export function localToday(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

/** Różnica w dniach kalendarzowych; liczona w UTC, więc zmiana czasu jej nie psuje. */
function daysBetween(fromIso: string, toIso: string): number {
  return Math.round((Date.parse(`${toIso}T00:00:00Z`) - Date.parse(`${fromIso}T00:00:00Z`)) / 86_400_000);
}

/** "07.11" w tym roku, "07.11.2027" w innym. */
export function shortDate(iso: string, today: string = localToday()): string {
  const [year, month, day] = iso.split("-");
  return `${day}.${month}${year === today.slice(0, 4) ? "" : `.${year}`}`;
}

export type DueTone = "overdue" | "today" | "soon" | "later";

export function isOverdue(task: { due_date: string | null; status: string }): boolean {
  if (!task.due_date || task.status === "done" || task.status === "trashed") return false;
  return daysBetween(localToday(), task.due_date) < 0;
}

/** Etykieta terminu: "po terminie 29.10", "dziś", "jutro", "07.11". */
export function dueInfo(iso: string, today: string = localToday()) {
  const short = shortDate(iso, today);
  const days = daysBetween(today, iso);
  const plainLabel = `termin ${short}`;
  if (days < 0) return { tone: "overdue" as DueTone, label: `po terminie ${short}`, plainLabel };
  if (days === 0) return { tone: "today" as DueTone, label: "termin dziś", plainLabel };
  if (days === 1) return { tone: "soon" as DueTone, label: "termin jutro", plainLabel };
  return { tone: (days <= 3 ? "soon" : "later") as DueTone, label: plainLabel, plainLabel };
}
